import React from 'react';
import type { Enrollment as EnrollmentModel } from 'models/enrollments/Enrollment';
import type { FormEnrollment as FormEnrollmentModel, FormEnrollmentDto } from 'models/enrollments/FormEnrollment';
import type { ProjectEstimationEnrollment } from 'models/enrollments/ProjectEstimationEnrollment';
import FormEnrollmentView from './FormEnrollmentView';
import ProjectEstimationEnrollmentView from './ProjectEstimationEnrollmentView';
import { ReviewEnrollment } from 'models/enrollments/ReviewEnrollment';
import ReviewEnrollmentView from './ReviewEnrollmentView';
import { EventEnrollment } from 'models/enrollments/EventEnrollment';
import EventEnrollmentView from './EventEnrollmentView';
import { ConsultationEnrollment } from 'models/enrollments/ConsultationEnrollment';
import ConsultationEnrollmentView from './ConsultationEnrollmentView';
import type { ReviewDecisionSubmission } from 'models/submissions/ReviewDecisionSubmission';
import type { ConsultationSubmission } from 'models/submissions/ConsultationSubmission';
import type { UserSubmissionDto } from 'models/projects/UserSubmissionDto';
import type { TeamProjectEstimation } from 'models/projects/TeamProjectEstimation';
import type { CurrentProjectStage } from 'models/projects/CurrentProjectStage';
import type { ProjectEstimationSubmissionDto } from 'models/projects/ProjectEstimationSubmission';
import type { NodeInstance } from 'models/nodes/NodeInstance';
import { NodeInstanceStatus } from 'models/nodes/NodeInstanceStatus';
import { NodeType } from 'models/nodes/NodeType';

type Props = {
  enrollment?: EnrollmentModel | FormEnrollmentModel | FormEnrollmentDto | ReviewEnrollment | EventEnrollment | ConsultationEnrollment | null;
  className?: string;
  onSubmit?: (submissionDto: UserSubmissionDto) => void;
  onTeamSubmit?: (estimation: TeamProjectEstimation) => void;
  workflowId?: string;
  nodeId?: string;
  projectId?: string;
  teamId?: string;
  isOwner?: boolean;
  isTeamMember?: boolean;
  teamEstimation?: TeamProjectEstimation | null;
  isDisabled?: boolean;
  canSubmitProjectEstimation?: boolean;
  currentStage?: CurrentProjectStage | null;
  onSubmitProjectEstimation?: (dto: ProjectEstimationSubmissionDto) => Promise<void> | void;
  onSubmitReviewDecision?: (submission: ReviewDecisionSubmission) => void;
  onSubmitConsultation?: (submission: ConsultationSubmission) => void;
};


export const EnrollmentView: React.FC<Props> = ({
  enrollment = null,
  className = '',
  onSubmit,
  onTeamSubmit,
  workflowId,
  nodeId,
  projectId,
  teamId,
  isOwner,
  isTeamMember,
  teamEstimation,
  isDisabled,
  canSubmitProjectEstimation,
  currentStage,
  onSubmitProjectEstimation,
  onSubmitReviewDecision,
  onSubmitConsultation,
}) => {
  if (!enrollment) return null;

  if (isReviewEnrollment(enrollment)) {
    const nodeInstance: NodeInstance = {
      id: nodeId ?? '',
      workflowInstanceId: workflowId ?? '',
      nodeTemplateId: '',
      order: 0,
      nodeType: NodeType.TeamReview,
      status: NodeInstanceStatus.Active,
      createdAt: '',
    };
    return (
      <ReviewEnrollmentView
        enrollment={enrollment}
        nodeInstance={nodeInstance}
        isDisabled={isDisabled}
      />
    );
  }

  if (isEventEnrollment(enrollment)) {
    return <EventEnrollmentView enrollment={enrollment} isDisabled={isDisabled} />;
  }

  if (isConsultationEnrollment(enrollment)) {
    return (
      <ConsultationEnrollmentView
        enrollment={enrollment as ConsultationEnrollment}
        onSubmit={onSubmitConsultation ?? (() => {})}
        isDisabled={isDisabled}
      />
    );
  }

  if (isFormEnrollmentModel(enrollment) || isFormEnrollmentDto(enrollment) || isFormEnrollment(enrollment)) {
    return <FormEnrollmentView enrollment={enrollment as any} className={className} onSubmit={onSubmit} workflowId={workflowId} nodeId={nodeId} projectId={projectId} teamId={teamId} isDisabled={isDisabled} />;
  }

  if (isProjectEstimationEnrollment(enrollment)) {
    return (
      <ProjectEstimationEnrollmentView
        enrollment={enrollment}
        teamEstimation={teamEstimation ?? null}
        isOwner={isOwner ?? false}
        isTeamMember={isTeamMember ?? false}
        teamId={teamId ?? ''}
        nodeId={nodeId ?? ''}
        projectId={projectId ?? ''}
        workflowId={workflowId ?? ''}
        onSubmit={onSubmit}
        onTeamSubmit={onTeamSubmit}
        canSubmit={canSubmitProjectEstimation}
        currentStage={currentStage}
        onSubmitProjectEstimation={onSubmitProjectEstimation}
        className={className}
      />
    );
  }

  console.log('Enrollment type not recognized, falling back to generic view. Enrollment:', enrollment);
  if (isFormEnrollmentModel(enrollment) || isFormEnrollmentDto(enrollment) || isFormEnrollment(enrollment)) {
    return <FormEnrollmentView enrollment={enrollment as any} className={className} onSubmit={onSubmit} workflowId={workflowId} nodeId={nodeId} projectId={projectId} teamId={teamId} isDisabled={isDisabled} />;
  }

  // fallback renderer
  const typeLabel = (enrollment as any)?.type ?? (enrollment as any)?.enrollmentType ?? 'unknown';
  return (
    <div className={`p-4 border rounded bg-white text-sm text-gray-600 ${className}`}>
      <div>No renderer for enrollment type: <code className="bg-gray-50 px-2 py-0.5 rounded">{typeLabel}</code></div>
      <pre className="mt-2 text-xs bg-gray-50 p-2 rounded overflow-auto">{JSON.stringify(enrollment, null, 2)}</pre>
    </div>
  );
};


const isProjectEstimationEnrollment = (e: unknown): e is ProjectEstimationEnrollment =>
  !!e && typeof e === 'object' && (e as any).type === 'Models.Enrollments.ProjectEstimationEnrollment';

const isFormEnrollmentDto = (e: unknown): e is FormEnrollmentDto => {
  return !!e && typeof e === 'object' && 'formId' in (e as any);
};

const isFormEnrollmentModel = (e: unknown): e is FormEnrollmentModel => {
  return !!e && typeof e === 'object' && (e as any).constructor?.name === 'FormEnrollment';
};

const isFormEnrollment = (enrollment: any): enrollment is FormEnrollmentModel => {
  return enrollment?.type === 'FormEnrollment'
    || enrollment?.enrollmentType === 'FormEnrollment';
};

const isReviewEnrollment = (enrollment: unknown): enrollment is ReviewEnrollment => {
  return !!enrollment
    && typeof enrollment === 'object'
    && ((enrollment as any).type === 'ReviewEnrollment'
      || (enrollment as any).enrollmentType === 'ReviewEnrollment'
      || enrollment instanceof ReviewEnrollment);
};

const isEventEnrollment = (enrollment: unknown): enrollment is EventEnrollment => {
  return !!enrollment
    && typeof enrollment === 'object'
    && ((enrollment as any).type === 'EventEnrollment'
      || (enrollment as any).enrollmentType === 'EventEnrollment'
      || enrollment instanceof EventEnrollment);
};

const isConsultationEnrollment = (enrollment: unknown): enrollment is ConsultationEnrollment => {
  return !!enrollment
    && typeof enrollment === 'object'
    && ((enrollment as any).type === 'ConsultationEnrollment'
      || (enrollment as any).enrollmentType === 'ConsultationEnrollment'
      || enrollment instanceof ConsultationEnrollment);
};

export default EnrollmentView;
